import { qboChargeRowDedupeKey } from '../../utils/qboChargeMapping'
import { normalizeChargeLabel, formatMonthKeyShortYear } from '../../utils/dashboardMath'

/** Chart.js plugin: draw a percent label on each slice (hides labels below minPct). */
export const pieSlicePercentLabels = (labelColor, minPct = 4) => ({
  id: 'chargeBreakdownPiePercents',
  afterDatasetsDraw (chart) {
    const t = chart.config.type
    if (t !== 'pie' && t !== 'doughnut') return
    const ds = chart.data.datasets?.[0]
    if (!ds?.data?.length) return
    const values = ds.data.map((v) => Number(v) || 0)
    const total = values.reduce((a, b) => a + b, 0)
    if (total <= 0) return

    const meta = chart.getDatasetMeta(0)
    if (!meta?.data?.length || meta.hidden) return

    const ctx = chart.ctx
    ctx.save()
    const fontSize = Math.min(12, Math.max(9, chart.width / 38))
    ctx.font = `600 ${fontSize}px system-ui, "Segoe UI", sans-serif`

    meta.data.forEach((arc, i) => {
      const value = values[i]
      const pct = (value / total) * 100
      if (pct < minPct) return
      const { x, y, startAngle, endAngle, innerRadius = 0, outerRadius = 0 } = arc
      if (outerRadius <= 0) return
      const mid = (startAngle + endAngle) / 2
      const r = innerRadius + (outerRadius - innerRadius) * 0.55
      const lx = x + Math.cos(mid) * r
      const ly = y + Math.sin(mid) * r
      const text = `${Math.round(pct)}%`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.lineJoin = 'round'
      ctx.miterLimit = 2
      ctx.lineWidth = Math.max(2, fontSize / 3.5)
      ctx.strokeStyle = 'rgba(0,0,0,0.5)'
      ctx.strokeText(text, lx, ly)
      ctx.fillStyle = labelColor
      ctx.fillText(text, lx, ly)
    })
    ctx.restore()
  }
})

export const BREAKDOWN_BY = {
  CHARGE_TYPE: 'chargeType',
  CLIENT: 'client',
  MONTH: 'month'
}

export const compareRows = (order, getCellValue) => (a, b) => {
  const av = getCellValue(a)
  const bv = getCellValue(b)
  const dir = order === 'asc' ? 1 : -1
  if (av == null && bv == null) return 0
  if (av == null) return 1
  if (bv == null) return -1
  if (typeof av === 'number' && typeof bv === 'number') {
    if (av === bv) return 0
    return av < bv ? -dir : dir
  }
  return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir
}

export const buildRowSearchBlob = (row) => {
  const monthKey = row?.billingMonthKey
  const parts = [
    row?.clientName,
    row?.chargeCategory,
    row?.chargeName,
    row?.invoiceNumber,
    monthKey,
    monthKey ? formatMonthKeyShortYear(monthKey) : '',
    row?.amount != null ? String(row.amount) : ''
  ]
  return parts
    .filter((p) => p != null && String(p).trim() !== '')
    .map((p) => String(p).toLowerCase())
    .join(' ')
}

export const rowMatchesSearch = (row, query) => {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return true
  const blob = buildRowSearchBlob(row)
  const tokens = q.split(/\s+/).filter(Boolean)
  return tokens.every((t) => blob.includes(t))
}

export const resolveChargeCategoryKey = (row, allChargeOptions, chargesPresentInData) => {
  const norm = normalizeChargeLabel(row?.chargeCategory || '')
  if (!norm) return ''
  if (chargesPresentInData && typeof chargesPresentInData.forEach === 'function') {
    for (const name of chargesPresentInData) {
      if (normalizeChargeLabel(name) === norm) return name
    }
  }
  const exact = (allChargeOptions || []).find((n) => normalizeChargeLabel(n) === norm)
  return exact || row.chargeCategory || ''
}

export const chargeRowIncludedInGraph = (
  row,
  allChargeOptions,
  chargesPresentInData,
  selectedCharges,
  excludedChargeRowKeys
) => {
  const dedupeKey = qboChargeRowDedupeKey(row)
  const catKey = resolveChargeCategoryKey(row, allChargeOptions, chargesPresentInData)
  const categoryInFilterList = Boolean(catKey && chargesPresentInData?.has?.(catKey))
  const categoryChecked = categoryInFilterList && Boolean(selectedCharges?.[catKey])
  return categoryChecked && !excludedChargeRowKeys[dedupeKey]
}

export const hueFromString = (label) => {
  const s = String(label || '')
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return h % 360
}

export const clientColumnColor = (
  clientSettings,
  row,
  categoryInFilterList,
  categoryChecked,
  fallbackTextColor,
  fadeColor
) => {
  const raw = row?.clientName && clientSettings?.[row.clientName]?.color
  if (!raw) return fallbackTextColor
  if (!categoryInFilterList) return fadeColor(raw, 0.92)
  if (!categoryChecked) return fadeColor(raw, 0.88)
  return raw
}

export const sortKeyForColumn = (row, columnId) => {
  switch (columnId) {
    case 'client':
      return row?.clientName || ''
    case 'chargeType':
      return row?.chargeCategory || ''
    case 'chargeName':
      return row?.chargeName || ''
    case 'invoice':
      return row?.invoiceNumber || ''
    case 'billingMonth':
      return row?.billingMonthKey || ''
    case 'amount':
    default:
      return Number(row?.amount) || 0
  }
}
